/**
 * CALGARY-SW — roster wave entries. GENERATED BY ASSEMBLY, WRITTEN BY REVIEW.
 *
 * Geography (streets, landmarks, coordinates, adjacency) ported from the source
 * roster; airspace derived by the calibrated classifier in
 * scripts/derive-airspace.mjs; every descriptive sentence written fresh from
 * aerial-media truth and fact-checked against its generation brief. See
 * docs/geo/VISION-v3.md for the fact contract these entries were written under.
 *
 * 23 entries. Edit by hand freely — assembly never overwrites a
 * file that has drifted from its fragments without --force.
 */
import type { Location } from "../types";

export const ROSTER_CALGARY_SW: Location[] = [
// ────────────────────────────────────────────────────────────── ALTADORE ──
  {
    slug: "altadore",
    name: "Altadore",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0143, lng: -114.1013 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 14,
      authorizationRequired: true,
      authorizationNote:
        "Altadore is about 14 km from Calgary International, near the CYYC control zone's charted boundary; a single address can fall either side of the line. The default plan treats it as controlled airspace until NAV Drone confirms otherwise, with authorisation filed before booking wherever it is required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "From above, Altadore reads as a grid interrupted once: the paired 33 Avenue SW and 34 Avenue SW carry Marda Loop's commercial strip straight through the residential blocks, and then the streets stop at the tree line of River Park.",
      consequence:
        "A ground gallery cannot show which end of that geometry a home sits at — steps from the shops or steps from the park edge photograph identically from the kerb, and the difference is what the buyer is paying for.",
      resolution:
        "A rising reveal that holds both Marda Loop and River Park in the establishing frame puts the position back into the listing, delivered in the standard turnaround.",
    },
    landmarks: ["Marda Loop", "River Park", "33 Avenue SW", "Elbow Drive SW", "Altadore School"],
    terrain:
      "A flat inner-city grid that ends abruptly at the wooded escarpment of River Park, where the land drops toward the river; the elevation lives at the eastern edge, not in the blocks themselves.",
    windNote:
      "Chinook belt like the rest of the city. The level grid offers no meaningful shelter, so a winter westerly arrives at full strength and the gust spread, not the average, decides the flying window.",
    seasonalWindow:
      "Mature street trees hold the frame green from late spring through early fall; after leaf-drop the grid opens up and infill rooflines read clearly. Low winter sun rakes long shadows across the avenues by mid-afternoon.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and pathways — River Park included — so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 15 minutes from the base. Same-day scheduling is realistic once the airspace picture is confirmed, and a booking pairs naturally with Garrison Woods, Currie or Killarney/Glengarry a few blocks away.",
    faqs: [
      {
        question: "Does a drone shoot in Altadore need NAV CANADA authorisation?",
        answer:
          "Assume yes until the address is checked. Altadore sits about 14 km from Calgary International, near the charted edge of the CYYC control zone, so which side of the line a property falls on decides the paperwork. Fly4MEdia plans it as controlled airspace and files any authorisation before the date is booked.",
      },
      {
        question: "Can one aerial frame show both Marda Loop and River Park?",
        answer:
          "Yes — that is the frame Altadore listings usually need. The commercial strip on 33 Avenue SW and the park's escarpment edge sit only blocks apart, so a single elevated composition can hold the home, the shops and the tree line together, which is the position a ground photo cannot prove.",
      },
    ],
    primaryKeywords: [
      "drone photography Altadore",
      "aerial photography Altadore",
      "Altadore drone services",
    ],
    nearestLocations: ["garrison-woods", "killarney-glengarry", "currie"],
  },

  // ──────────────────────────────────────────────────────────── BRIDLEWOOD ──
  {
    slug: "bridlewood",
    name: "Bridlewood",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9138, lng: -114.087 },
    tier: 2,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 16,
      authorizationRequired: false,
      authorizationNote:
        "Bridlewood lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 16 km from Kirkby Field (Indus). No airspace authorisation is normally required. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Bridlewood holds the city's southern rim, and west of Sheriff King Street SW little stands between the rooflines and open country — a chinook that reads as a breeze in the inner city arrives here with its gust spread intact.",
      consequence:
        "A shoot scheduled off a central Calgary forecast can be legal, sunny and unflyable all at once; the crew drives 25 minutes south and stands the aircraft down at the kerb.",
      resolution:
        "Flight windows for Bridlewood are set against on-site wind rather than city averages — usually an early-morning or golden-hour hold when the gradient drops — and the frames are delivered in the standard turnaround.",
    },
    landmarks: [
      "Fish Creek Provincial Park",
      "Somerset-Bridlewood LRT Station",
      "Bridlewood Market",
      "Sheriff King Street SW",
    ],
    terrain:
      "A shallow south-sloping table of crescents and closes at the city's edge, with the wooded valley of Fish Creek Provincial Park marking the low ground to the north.",
    windNote:
      "Fully exposed chinook belt. With open ground to the west and south, westerlies arrive unbroken; no valley funnels or shelters here, so the regional gradient plus its gusts is the honest planning number.",
    seasonalWindow:
      "South-sloping and lightly treed, so snow releases early and the green-up shows quickly in spring frames. Long summer evenings put golden hour late, and low winter sun keeps rooflines modelled for most of the day.",
    bylawNote:
      "Takeoff and landing inside municipal parks and pathways is restricted under City of Calgary parks bylaws; flights here launch from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 25 minutes from the base — a comfortable same-day booking. Somerset, Evergreen, Silverado and Shawnessy sit within a few minutes' drive, so multi-property runs across the deep southwest schedule efficiently.",
    faqs: [
      {
        question: "Is airspace authorisation needed for drone work in Bridlewood?",
        answer:
          "Normally no. Bridlewood lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 16 km from Kirkby Field at Indus, so no authorisation is usually required. The specific site and altitude are still confirmed against NAV Drone before every flight, which keeps the booking clean.",
      },
      {
        question: "Why would a Bridlewood shoot be rescheduled when central Calgary looks calm?",
        answer:
          "Because the southern rim takes the wind first. With open country west of Sheriff King Street SW, gusts here run stronger than an inner-city forecast suggests. Fly4MEdia sets the window from on-site conditions rather than a city average, and a weather hold simply moves the date, never the deliverables.",
      },
    ],
    primaryKeywords: [
      "drone photography Bridlewood",
      "aerial photography Bridlewood",
      "Bridlewood drone services",
    ],
    nearestLocations: ["somerset", "evergreen", "silverado", "shawnessy"],
  },

  // ──────────────────────────────────────────────────────────── COACH HILL ──
  {
    slug: "coach-hill",
    name: "Coach Hill",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0498, lng: -114.1719 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 14,
      authorizationRequired: true,
      authorizationNote:
        "Coach Hill is about 14 km from Calgary International, near the CYYC control zone's charted boundary; a single address can fall either side of the line. The default plan treats it as controlled airspace until NAV Drone confirms otherwise, with authorisation filed before booking wherever it is required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Coach Hill's streets ride a shoulder of high ground, and frames pointed north across the Bow Trail pathway pick up the river valley below — water the neighbourhood itself never sees from the kerb.",
      consequence:
        "Shot from the street, a Coach Hill listing is a hillside house with no context; the buyer never learns that the lot rides high enough to borrow the valley.",
      resolution:
        "An elevated hold above the property, composed north over the Bow Trail pathway, puts the water and the high ground into one frame — delivered in the standard turnaround.",
    },
    landmarks: ["Bow Trail pathway", "Canada Olympic Park", "Coach Hill School", "Coachway Road SW"],
    terrain:
      "A high shoulder of west Calgary that falls away northward toward the river valley; streets curve along the contour rather than running as a grid, so the lots step with the slope.",
    windNote:
      "High ground in the chinook belt. Westerlies reach Coach Hill early and unbroken, and the slope adds mechanical turbulence on the lee side, so calm-morning windows are worth protecting.",
    seasonalWindow:
      "North-facing slopes hold snow and shadow longer than the flat grid below, which keeps winter frames graphic; in summer the late, low golden hour models the contour streets instead of flattening them.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone launches and landings on municipal park and pathway land, so takeoff is staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 15 minutes west of the base. Patterson, Signal Hill and Cougar Ridge are minutes away, so a Coach Hill booking pairs well into a west-side run on the same authorisation footing.",
    faqs: [
      {
        question: "Which control zone applies to drone flights in Coach Hill?",
        answer:
          "Coach Hill sits about 14 km from Calgary International, near the charted boundary of the CYYC control zone — close enough that the exact address decides the paperwork. Fly4MEdia plans it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the shoot date is booked.",
      },
      {
        question: "Can an aerial photo show the river valley from a Coach Hill property?",
        answer:
          "Often, yes. The community rides a shoulder of high ground, so an elevated frame composed north across the Bow Trail pathway can carry the valley in the background of a listing shot. Whether it works for a specific lot depends on position and legal altitude, both checked during planning.",
      },
    ],
    primaryKeywords: [
      "drone photography Coach Hill",
      "aerial photography Coach Hill",
      "Coach Hill drone services",
    ],
    nearestLocations: ["patterson", "signal-hill", "cougar-ridge"],
  },

  // ────────────────────────────────────────────────────────── COUGAR RIDGE ──
  {
    slug: "cougar-ridge",
    name: "Cougar Ridge",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0574, lng: -114.2136 },
    tier: 3,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 16,
      authorizationRequired: true,
      authorizationNote:
        "At about 16 km from Calgary International, Cougar Ridge runs close to the charted edge of the CYYC control zone — which side of the line an address falls on decides the paperwork. Planning treats it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Cougar Ridge is laid out across the Paskapoo Slopes, so from above its crescents run in contour lines and the rooflines step downhill in tiers, with the WinSport ski runs cutting the hillside immediately beside them.",
      consequence:
        "Ground photography treats each house as level ground; the tiered setting — and the fact that a backyard can look onto a ski hill — never makes it into the gallery.",
      resolution:
        "A boundary-line orbit along the slope holds the roofline tiers and the WinSport runs in a single move, delivered in the standard turnaround.",
    },
    landmarks: [
      "WinSport / Canada Olympic Park",
      "Paskapoo Slopes",
      "Cougar Ridge Drive SW",
      "West Springs Shopping Centre",
    ],
    terrain:
      "Benched hillside on the Paskapoo Slopes: streets follow the contour, lots step in tiers, and the grade runs down toward the valley on the community's north side.",
    windNote:
      "Chinook belt, with the slope adding its own behaviour — wind coming off the higher ground to the west works the brow of the hillside harder than the flats below, so gust spread sets the window.",
    seasonalWindow:
      "Snow lingers on the ski runs beside the community after the streets are bare, which gives winter frames a graphic edge; in summer, evening light rakes across the tiers and separates the rooflines.",
    bylawNote:
      "Municipal parks and pathways are off-limits for drone takeoff and landing under City of Calgary parks bylaws, so every launch here is staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "construction-progress"],
    mobilisationNote:
      "About 20 minutes from the base. West Springs, Aspen Woods, Discovery Ridge and Patterson all sit within the same west-side corridor, so pairing two shoots on one mobilisation is straightforward.",
    faqs: [
      {
        question: "Do you need special permission to fly a drone over Cougar Ridge?",
        answer:
          "Yes — plan on it. Cougar Ridge sits about 16 km from Calgary International, near the charted boundary of the CYYC control zone, so which side of the line a given address falls on decides the paperwork. Fly4MEdia plans it as controlled airspace and files any required authorisation before the shoot date is booked.",
      },
      {
        question: "Will WinSport appear in aerial photos of a Cougar Ridge home?",
        answer:
          "For many lots, yes. The community is built along the Paskapoo Slopes directly beside the WinSport runs, so an orbit or elevated still can hold the house and the ski hill in one composition. Framing is planned per address, since position on the slope decides what the camera sees.",
      },
    ],
    primaryKeywords: [
      "drone photography Cougar Ridge",
      "aerial photography Cougar Ridge",
      "Cougar Ridge drone services",
    ],
    nearestLocations: ["west-springs", "aspen-woods", "discovery-ridge", "patterson"],
  },

  // ──────────────────────────────────────────────────────────────── CURRIE ──
  {
    slug: "currie",
    name: "Currie",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0158, lng: -114.1204 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 14,
      authorizationRequired: true,
      authorizationNote:
        "Currie is about 14 km from Calgary International, near the CYYC control zone's charted boundary; a single address can fall either side of the line. The default plan treats it as controlled airspace until NAV Drone confirms otherwise, with authorisation filed before booking wherever it is required. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The Currie Barracks Parade Square is kept open and level in the middle of a district that is otherwise filling in, so evening light crosses it uninterrupted and throws the surrounding new rooflines into long relief.",
      consequence:
        "Shoot at midday and the district flattens into plan view — the square reads as empty ground and a new build on Vimy Way SW looks like every other elevation on the block.",
      resolution:
        "A golden-hour hold timed to the low light puts shadow and depth back into the frame, with the square as the anchor — delivered in the standard turnaround.",
    },
    landmarks: [
      "Currie Barracks Parade Square",
      "Mount Royal University",
      "Vimy Way SW",
      "Flanders Avenue SW",
      "Buchanan Park",
    ],
    terrain:
      "Level former airfield ground: open, regular blocks around the preserved parade square, with Mount Royal University's campus holding the district's southern edge.",
    windNote:
      "Chinook belt with no terrain shelter — the ground here is flat by design. A westerly crosses the open square at full strength, so winter windows follow the gust spread rather than the daily average.",
    seasonalWindow:
      "The open square and wide streets photograph cleanly in every season, but low-angle light — early or late — is when the layout reads as geometry instead of pavement. Snow cover holds evenly on the level ground.",
    bylawNote:
      "City of Calgary parks bylaws restrict takeoff and landing in municipal parks and on pathways, so launches are arranged from private property with the client's consent.",
    subjectMix: [
      "new-build-subdivision",
      "construction-progress",
      "commercial-retail",
      "luxury-residential",
    ],
    mobilisationNote:
      "About 15 minutes from the base, with Garrison Woods, Altadore and Killarney/Glengarry adjoining — one mobilisation can cover a listing and its comparables in the same light.",
    faqs: [
      {
        question: "Is Currie inside the Calgary International control zone?",
        answer:
          "It sits close to the line. Currie is about 14 km from Calgary International, near the charted boundary of the CYYC control zone, so the exact address decides the paperwork. Fly4MEdia plans it as controlled airspace until NAV Drone shows otherwise and files any authorisation before the date is booked.",
      },
      {
        question: "What time of day photographs Currie best from the air?",
        answer:
          "The first and last hours of light. Currie's ground is deliberately flat and open around the parade square, so midday sun flattens it into plan view. Low-angle light restores depth — shadows draw the block pattern, and new construction on streets like Flanders Avenue SW gains real modelling.",
      },
      {
        question: "Can you document construction progress in Currie?",
        answer:
          "Yes. The district is still building out, and repeat aerial passes flown from matching positions and altitudes produce a clean progress series for builders and marketers. Airspace planning is handled as part of each booking, so the series stays on schedule from the first visit to the final frame.",
      },
    ],
    primaryKeywords: [
      "drone photography Currie",
      "aerial photography Currie",
      "Currie drone services",
    ],
    nearestLocations: ["garrison-woods", "altadore", "killarney-glengarry"],
  },

  // ─────────────────────────────────────────────────────── DISCOVERY RIDGE ──
  {
    slug: "discovery-ridge",
    name: "Discovery Ridge",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0393, lng: -114.2113 },
    tier: 3,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Controlled — between CYYC and CYBW",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 13,
      authorizationRequired: true,
      authorizationNote:
        "Discovery Ridge sits in the same airspace corridor as Aspen Woods, about 1 km away, where flights are planned as controlled airspace. Work here is conducted under an Advanced RPAS certificate, with a NAV CANADA RPAS authorisation obtained wherever the specific site requires one. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Discovery Ridge sits low, not high — the community is tucked against the valley floor beside Griffith Woods Park, so from above the forest and the river flats surround the streets instead of spreading out beneath them.",
      consequence:
        "Compose it like a hilltop community and the frame misses the point — the enclosure is the product here, and from too high the canopy of Griffith Woods Park flattens into texture instead of reading as a boundary.",
      resolution:
        "A low elevated hold along the line of the Elbow River pathway keeps the tree line standing in the frame, so the valley-floor setting reads as shelter rather than flat ground — delivered in the standard turnaround.",
    },
    landmarks: [
      "Griffith Woods Park",
      "Elbow River pathway",
      "Discovery Ridge Boulevard SW",
      "West Springs Shopping Centre",
    ],
    terrain:
      "Valley-floor ground beneath the west-side plateau: streets sit level against the forest of Griffith Woods Park, with the grade rising away from the river flats toward the communities above.",
    windNote:
      "Chinook belt, but the valley position matters — the ground sits below the plateau, and the tree stands of Griffith Woods Park break the surface wind, so conditions here often run gentler than on the exposed high ground nearby.",
    seasonalWindow:
      "The conifer canopy holds colour through winter, so frames keep contrast even under snow; in summer, late light slides down the valley side and reaches the streets last, which shortens — and sweetens — the golden-hour window.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing on park and pathway land — Griffith Woods Park included — so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision"],
    mobilisationNote:
      "About 20 minutes from the base. Springbank Hill, Aspen Woods and Signal Hill sit directly above on the plateau, so paired bookings across the west side are easy to schedule.",
    faqs: [
      {
        question: "What airspace rules cover a drone shoot in Discovery Ridge?",
        answer:
          "The area is planned as controlled airspace — it lies in the corridor between the Calgary International and Springbank control zones, about 13 km from Springbank Airport. Work is flown under an Advanced RPAS certificate, with a NAV CANADA authorisation obtained wherever the specific site requires one, confirmed against NAV Drone first.",
      },
      {
        question: "Can you fly over Griffith Woods Park for a Discovery Ridge listing?",
        answer:
          "The park does not need to be overflown to appear in the frame. Launches are staged from private property, and the forest reads clearly from a lawful position beside it. City of Calgary parks bylaws restrict takeoff and landing on park ground, and flight lines are planned to respect that.",
      },
    ],
    primaryKeywords: [
      "drone photography Discovery Ridge",
      "aerial photography Discovery Ridge",
      "Discovery Ridge drone services",
    ],
    nearestLocations: ["springbank-hill", "aspen-woods", "signal-hill"],
  },

  // ───────────────────────────────────────────────────────────── EVERGREEN ──
  {
    slug: "evergreen",
    name: "Evergreen",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.913, lng: -114.1074 },
    tier: 2,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 17,
      authorizationRequired: false,
      authorizationNote:
        "Evergreen sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 17 km from Kirkby Field (Indus). Authorisation is not normally required here. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "From the air, Evergreen's road network tells you where the city stops: the through-routes feed Evergreen Boulevard SW and Fish Creek Boulevard SW, and along the community's northern edge the streets end against the tree line of Fish Creek Provincial Park.",
      consequence:
        "A street-level gallery cannot show which side of that network a home sits on — backing the park and backing the boulevard photograph identically from the kerb, and the difference is exactly what the buyer is weighing.",
      resolution:
        "An elevated still composed along the park boundary places the lot in the network — home, street and tree line in one legible frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "Fish Creek Provincial Park",
      "Evergreen Boulevard SW",
      "Fish Creek Boulevard SW",
      "Shawnessy Shopping Centre",
    ],
    terrain:
      "Gently rolling table land at the city's southwest edge, sloping toward the wooded valley of Fish Creek Provincial Park; the relief is modest until the ground breaks at the park line.",
    windNote:
      "Chinook belt with open exposure to the west and south; no sheltering terrain stands upwind until the Fish Creek valley itself, so wind at Evergreen tracks the regional gradient plus its gusts.",
    seasonalWindow:
      "The park's aspens flare in fall and read as a solid band of colour against the rooflines; after leaf-drop the valley opens up and winter frames trade colour for structure. Summer golden hour arrives late and lingers.",
    bylawNote:
      "Drone takeoff and landing in municipal parks and pathways is restricted by City of Calgary parks bylaws; launches are staged from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 25 minutes from the base — still a same-day booking. Shawnessy, Bridlewood, Silverado and Somerset adjoin, so several southwest properties can share one mobilisation.",
    faqs: [
      {
        question: "Do drone flights over Evergreen need clearance from NAV CANADA?",
        answer:
          "Normally not. Evergreen lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 17 km from Kirkby Field at Indus, so no airspace authorisation is usually required. Fly4MEdia still confirms the specific site and altitude against NAV Drone before every flight as standard practice.",
      },
      {
        question: "Is it possible to photograph a home backing Fish Creek Provincial Park?",
        answer:
          "Yes, and it is the frame worth ordering. Launch happens from private property with the owner's consent — parks bylaws restrict takeoff and landing on park ground — and the flight line is planned so the lot, the street and the tree line sit together in one composition.",
      },
    ],
    primaryKeywords: [
      "drone photography Evergreen",
      "aerial photography Evergreen",
      "Evergreen drone services",
    ],
    nearestLocations: ["shawnessy", "bridlewood", "silverado", "somerset"],
  },

  // ──────────────────────────────────────────────────────── GARRISON WOODS ──
  {
    slug: "garrison-woods",
    name: "Garrison Woods",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0134, lng: -114.1046 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 14,
      authorizationRequired: true,
      authorizationNote:
        "At about 14 km from Calgary International, Garrison Woods runs close to the charted edge of the CYYC control zone — which side of the line an address falls on decides the paperwork. Planning treats it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Garrison Woods holds no water of its own, but it sits close enough to River Park that an elevated frame gains the green corridor and the water beyond it — borrowed scenery the street grid keeps hidden.",
      consequence:
        "Shot from the ground, a Garrison Woods listing is architecture against sky; the park and the water that anchor the district's appeal sit two frames away and never appear.",
      resolution:
        "A rising reveal from rooftop height, composed east toward River Park, brings the green corridor and the water into the listing's establishing shot — delivered in the standard turnaround.",
    },
    landmarks: ["River Park", "Garrison Woods Shopping Centre", "Marda Loop", "Sabre Street SW"],
    terrain:
      "Level redeveloped ground with a planned street pattern — greens and squares set inside the blocks — holding flat until the land drops beyond River Park to the east.",
    windNote:
      "Chinook belt on flat, open ground. Winter westerlies arrive without terrain to slow them, and the district's low, even rooflines give little mechanical shelter; the gust spread sets the flying window.",
    seasonalWindow:
      "Street trees are young enough that the layout reads from the air year-round; snow renders the greens and squares as crisp geometry, and long June evenings put soft light onto west-facing façades.",
    bylawNote:
      "Under City of Calgary parks bylaws, municipal parks and pathways are not available for drone takeoff or landing, so flights launch from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the base, sharing boundaries with Altadore and Currie — a single mobilisation covers all three, useful when an agent lists across the district.",
    faqs: [
      {
        question: "How does the CYYC control zone affect drone shoots in Garrison Woods?",
        answer:
          "It puts the paperwork in play. Garrison Woods sits about 14 km from Calgary International, near the charted boundary of the CYYC control zone, so which side of the line an address falls on decides the requirement. Fly4MEdia plans it as controlled airspace and files any authorisation before booking the date.",
      },
      {
        question: "Can aerial photos of Garrison Woods include River Park?",
        answer:
          "Usually, yes. The park sits just east of the district, so an elevated composition flown from a lawful position over private property can hold the home, the street pattern and the park's green corridor in one frame. The exact geometry is confirmed per address during flight planning.",
      },
    ],
    primaryKeywords: [
      "drone photography Garrison Woods",
      "aerial photography Garrison Woods",
      "Garrison Woods drone services",
    ],
    nearestLocations: ["altadore", "currie", "killarney-glengarry"],
  },

  // ───────────────────────────────────────────────────────────── GLAMORGAN ──
  {
    slug: "glamorgan",
    name: "Glamorgan",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0089, lng: -114.1274 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 15,
      authorizationRequired: true,
      authorizationNote:
        "Glamorgan sits in the same airspace corridor as Calgary, about 6 km away, where flights are planned as controlled airspace. Work here is conducted under an Advanced RPAS certificate, with a NAV CANADA RPAS authorisation obtained wherever the specific site requires one. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Beyond 37 Street SW the city's fabric thins toward open ground, so the west wind reaches Glamorgan with little in its way — a chinook day here is windier than the same day in the sheltered inner grid.",
      consequence:
        "The client books off a citywide forecast; the airframe answers to the wind at the kerb. On the wrong afternoon those two disagree, and the difference is a rescheduled shoot.",
      resolution:
        "Wind windows for Glamorgan are read on site and held flexibly — often first light, before the gradient builds — so the frames are flown steady and delivered in the standard turnaround.",
    },
    landmarks: [
      "Optimist Athletic Park",
      "37 Street SW",
      "Glamorgan Community Centre",
      "Southland Leisure Centre",
    ],
    terrain:
      "A level, mature grid on the city's western rim: curving residential streets inside a rectangular frame of arterials, with the open green of Optimist Athletic Park breaking the rooftop pattern.",
    windNote:
      "Chinook belt, more open than the inner grid: the sheltering fabric thins past 37 Street SW, so flying windows here are read on site rather than assumed from a citywide forecast.",
    seasonalWindow:
      "Mature trees close the canopy over summer frames and drop it again in October; winter's low sun and early dusk suit the western edge, where the last light lands unobstructed.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and pathways, so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the base. Killarney/Glengarry, Lakeview, North Glenmore Park and Coach Hill all adjoin or sit minutes away, so west-side pairings are simple to arrange.",
    faqs: [
      {
        question: "Does drone photography in Glamorgan require an airspace authorisation?",
        answer:
          "Yes — Glamorgan is flown as controlled airspace. It shares Calgary's corridor within the CYYC control zone, so work is conducted under an Advanced RPAS certificate, with a NAV CANADA authorisation obtained wherever the specific site requires one. The site and altitude are confirmed against NAV Drone before every flight.",
      },
      {
        question: "What does an aerial frame show of Glamorgan that a street photo cannot?",
        answer:
          "The plan itself. Glamorgan's curving residential streets sit inside a clean rectangle of arterials, with Optimist Athletic Park breaking the roofline pattern in green — a composition that only reads from altitude. For a listing or a facility like Southland Leisure Centre, that context frame is the difference between a building and a setting.",
      },
    ],
    primaryKeywords: [
      "drone photography Glamorgan",
      "aerial photography Glamorgan",
      "Glamorgan drone services",
    ],
    nearestLocations: ["killarney-glengarry", "lakeview", "north-glenmore-park", "coach-hill"],
  },

  // ─────────────────────────────────────────────────── KILLARNEY/GLENGARRY ──
  {
    slug: "killarney-glengarry",
    name: "Killarney/Glengarry",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0369, lng: -114.1181 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 12,
      authorizationRequired: true,
      authorizationNote:
        "Killarney/Glengarry is about 12 km from Calgary International, near the CYYC control zone's charted boundary; a single address can fall either side of the line. The default plan treats it as controlled airspace until NAV Drone confirms otherwise, with authorisation filed before booking wherever it is required. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Killarney/Glengarry is flat enough that its rooflines run in level rows for block after block — until the ground quits at Shaganappi Point Golf Course, where the grid stops on the lip of the valley.",
      consequence:
        "That evenness is the trap: from altitude, one infill looks like the next, and a frame with no relief in it gives the buyer nothing to place the property against.",
      resolution:
        "A low orbit that keeps the valley edge beyond Shaganappi Point Golf Course in the background hands the flat grid a horizon — separation a ground photographer cannot buy — delivered in the standard turnaround.",
    },
    landmarks: [
      "Shaganappi Point Golf Course",
      "Killarney Park",
      "Westbrook LRT Station",
      "26 Avenue SW",
      "Richmond Road SW",
    ],
    terrain:
      "A level inner-city grid of long east–west blocks, with the only meaningful break in relief at its northern margin, where the land falls away past Shaganappi Point Golf Course.",
    windNote:
      "Chinook belt. The flat grid neither funnels nor shelters, so surface wind tracks the regional gradient; winter gust spread is the number that decides whether the aircraft flies.",
    seasonalWindow:
      "Infill construction keeps the rooftop pattern changing year to year, and leaf-off months show it best; in summer the mature canopy softens the grid and the long evenings light the avenues end-on.",
    bylawNote:
      "Takeoff and landing on municipal park and pathway land is restricted under City of Calgary parks bylaws, so launch points are arranged on private property with the client's consent.",
    subjectMix: ["luxury-residential", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 15 minutes from the base, bordered by Altadore, Garrison Woods and Glamorgan — the kind of geography where two listings share one mobilisation without moving the car far.",
    faqs: [
      {
        question: "Are drones allowed in Killarney/Glengarry without authorisation?",
        answer:
          "Treat it as controlled until checked. The community sits about 12 km from Calgary International, near the charted boundary of the CYYC control zone, so the specific address determines whether authorisation applies. Fly4MEdia plans it as controlled airspace, confirms against NAV Drone, and files any authorisation before booking.",
      },
      {
        question: "How do aerial photos help an infill listing in Killarney/Glengarry?",
        answer:
          "By giving a flat grid a horizon. The blocks here read level and uniform from above, so Fly4MEdia frames low, with the valley edge beyond Shaganappi Point Golf Course in the background — the property gains context and separation that street-level photography of a level grid cannot produce.",
      },
    ],
    primaryKeywords: [
      "drone photography Killarney/Glengarry",
      "aerial photography Killarney/Glengarry",
      "Killarney/Glengarry drone services",
    ],
    nearestLocations: ["altadore", "garrison-woods", "glamorgan"],
  },

  // ────────────────────────────────────────────────────────────── LAKEVIEW ──
  {
    slug: "lakeview",
    name: "Lakeview",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9986, lng: -114.1266 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 16,
      authorizationRequired: true,
      authorizationNote:
        "Lakeview sits in the same airspace corridor as Calgary, about 6 km away, where flights are planned as controlled airspace. Work here is conducted under an Advanced RPAS certificate, with a NAV CANADA RPAS authorisation obtained wherever the specific site requires one. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Lakeview borders the Glenmore Reservoir where the Weaselhead Flats feed into it, so an aerial frame here can hold open water, the braided channels of the flats and the tree line in one composition.",
      consequence:
        "None of that survives a ground-level shoot. From the street, the reservoir is a rumour behind the last row of houses; the buyer paying for proximity to water never sees the water.",
      resolution:
        "A rising reveal from the property toward the reservoir puts the shoreline into the establishing frame at a lawful distance and altitude — delivered in the standard turnaround.",
    },
    landmarks: [
      "Glenmore Reservoir",
      "Weaselhead Flats",
      "North Glenmore Park",
      "Earl Grey Golf Club",
      "Lakeview Drive SW",
    ],
    terrain:
      "A level plateau of streets that ends at the reservoir's escarpment; the interesting ground is all at the margin, where the land steps down to the water and the Weaselhead Flats spread west.",
    windNote:
      "Chinook belt with a water edge. Westerlies cross the open reservoir with no friction, so the shoreline blocks feel the wind before the interior streets do; gust spread over the water sets the margin of the window.",
    seasonalWindow:
      "Ice-on and ice-off change the reservoir's whole character — white geometry in winter, open water in summer — and either reads well from altitude. Evening light comes across the water and lands on Lakeview's western edge first.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and pathways — North Glenmore Park among them — so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the base. North Glenmore Park, Glamorgan and Killarney/Glengarry adjoin, so reservoir-adjacent shoots pair readily with the rest of the inner southwest.",
    faqs: [
      {
        question: "Can you legally fly a drone near the Glenmore Reservoir in Lakeview?",
        answer:
          "Yes, with planning. Lakeview is flown as controlled airspace under an Advanced RPAS certificate, with a NAV CANADA authorisation obtained wherever the specific site requires one and every site confirmed against NAV Drone first. Launch points stay on private property, since parks bylaws restrict takeoff and landing on park ground.",
      },
      {
        question: "Will the reservoir appear in drone photos of a Lakeview home?",
        answer:
          "For most addresses, yes. The community ends at the reservoir's escarpment, so an elevated frame composed toward the water carries the shoreline — and often the Weaselhead Flats — behind the property. How prominently depends on the lot's distance from the edge, which is assessed during flight planning.",
      },
    ],
    primaryKeywords: [
      "drone photography Lakeview",
      "aerial photography Lakeview",
      "Lakeview drone services",
    ],
    nearestLocations: ["north-glenmore-park", "glamorgan", "killarney-glengarry"],
  },

  // ────────────────────────────────────────────────────────────── MILLRISE ──
  {
    slug: "millrise",
    name: "Millrise",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9316, lng: -114.1006 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 17,
      authorizationRequired: false,
      authorizationNote:
        "Millrise sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 17 km from Kirkby Field (Indus). Authorisation is not normally required here. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "From above, Millrise is a study in the loop-and-cul-de-sac era: crescents coil off Millrise Drive SW and Millrise Boulevard SW with few through streets, and the rail line clips the community's eastern edge at Millrise LRT Station.",
      consequence:
        "That geometry defeats ground photography twice over — a buyer cannot tell a quiet interior crescent from a collector road, and cannot see how close the home actually sits to the train.",
      resolution:
        "A single elevated still answers both: the loop pattern, the lot and Millrise LRT Station in one frame, flown and delivered in the standard turnaround.",
    },
    landmarks: [
      "Millrise LRT Station",
      "Fish Creek Provincial Park",
      "Millrise Drive SW",
      "162 Avenue SW",
      "Millrise Park",
    ],
    terrain:
      "Gently sloping suburban ground between 162 Avenue SW and the wooded valley of Fish Creek Provincial Park; the street pattern, not the topography, gives the aerial frame its structure.",
    windNote:
      "Chinook belt at the city's south end, with gradient wind arriving off the higher ground to the west; nothing in the local terrain funnels or blocks it, so forecast plus gust spread is the honest planning number.",
    seasonalWindow:
      "The valley of Fish Creek Provincial Park to the north carries the seasonal colour — green, gold, then bare — while the crescents themselves photograph most cleanly under low light or fresh snow, when the loop geometry draws itself.",
    bylawNote:
      "Municipal park and pathway land is restricted for drone takeoff and landing under City of Calgary parks bylaws; launches here are staged from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the base. Somerset, Shawnessy, Woodbine and Evergreen surround it, so a Millrise booking slots into any deep-southwest run on the same trip.",
    faqs: [
      {
        question: "What airspace does Millrise fall under for drone operations?",
        answer:
          "Uncontrolled Class G. Millrise lies outside the charted Calgary-area control zones, about 17 km from Kirkby Field at Indus, so no airspace authorisation is normally required. Fly4MEdia still confirms the specific site and altitude against NAV Drone before every flight, so the booking carries no paperwork surprises.",
      },
      {
        question: "Does proximity to the LRT matter in Millrise aerial photos?",
        answer:
          "It is often the question a buyer wants answered. An elevated frame shows honestly how the lot relates to Millrise LRT Station and the rail line at the community's edge — walkable convenience for some listings, comfortable distance for others. Either way the photograph settles what a map cannot.",
      },
    ],
    primaryKeywords: [
      "drone photography Millrise",
      "aerial photography Millrise",
      "Millrise drone services",
    ],
    nearestLocations: ["somerset", "shawnessy", "woodbine", "evergreen"],
  },
// ─────────────────────────────────────────────── NORTH GLENMORE PARK ──
  {
    slug: "north-glenmore-park",
    name: "North Glenmore Park",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0072, lng: -114.112 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 15,
      authorizationRequired: true,
      authorizationNote:
        "North Glenmore Park sits in the same airspace corridor as Calgary, about 5 km away, where flights are planned as controlled airspace. Work here is conducted under an Advanced RPAS certificate, with a NAV CANADA RPAS authorisation obtained wherever the specific site requires one. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "In the last hour of a clear day the low sun crosses the Weaselhead Natural Area and lights the Glenmore Reservoir from the west, so the water below North Glenmore Drive SW turns briefly from flat grey to lit metal before the light goes.",
      consequence:
        "A midday shoot wastes the community's best asset — the reservoir photographs as a dull field and the rim reads as an ordinary street — while the hour the water actually performs is short and moves through the year.",
      resolution:
        "The shoot is booked to that hour: a golden-hour hold above the reservoir rim, with the NAV CANADA authorisation filed beforehand so the light window is the only variable left, delivered in the standard turnaround.",
    },
    landmarks: [
      "Glenmore Reservoir",
      "Weaselhead Natural Area",
      "Glenmore Athletic Park",
      "Earl Grey Golf Club",
      "North Glenmore Drive SW",
    ],
    terrain:
      "A level table on the north rim of the Glenmore Reservoir. The playing fields of Glenmore Athletic Park and the fairways of Earl Grey Golf Club hold the flat ground, and the land drops to open water and the Weaselhead flats along the community's southern edge.",
    windNote:
      "Chinook belt, with a long open fetch: west wind crosses the reservoir and the Weaselhead flats before it reaches the rim, so gusts arrive at the community's edge unsoftened by any terrain.",
    seasonalWindow:
      "The reservoir sets the calendar. Open water from spring through fall gives the south edge its reflections; in winter the frozen surface reads as a white plain. Evening light over the Weaselhead is the reliable peak in every season.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and along pathways, so launches around North Glenmore Park are staged from private property with the client's consent.",
    subjectMix: ["lakefront", "luxury-residential", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the Calgary base, so a shoot here holds a light window easily and pairs on one mobilisation with Lakeview, Glamorgan or Killarney-Glengarry.",
    faqs: [
      {
        question: "Do drone flights over North Glenmore Park need airport authorisation?",
        answer:
          "Usually, yes. North Glenmore Park sits in the Calgary airspace corridor, planned as controlled Class C airspace for Calgary International. Flights run under an Advanced RPAS certificate, with a NAV CANADA authorisation obtained wherever the specific site requires one, and every site and altitude is confirmed against NAV Drone before flight.",
      },
      {
        question: "When does the Glenmore Reservoir look best in aerial photographs?",
        answer:
          "In the last hour of direct light, from spring through fall. Low western sun coming over the Weaselhead Natural Area puts reflections on the open water, which separates the rim properties from an otherwise flat frame. Midday light flattens the reservoir to grey, so shoots here are booked against the evening window.",
      },
      {
        question: "How quickly can a North Glenmore Park shoot be scheduled?",
        answer:
          "The community is about 15 minutes from the base, so mobilisation is trivial; the pacing item is the NAV CANADA authorisation, which is filed as part of booking. Once it is in hand, the date moves with the weather — and a visit here pairs easily with Lakeview or Glamorgan.",
      },
    ],
    primaryKeywords: [
      "drone photography North Glenmore Park",
      "aerial photography North Glenmore Park",
      "North Glenmore Park drone services",
    ],
    nearestLocations: ["lakeview", "glamorgan", "killarney-glengarry"],
  },

  // ──────────────────────────────────────────────────────────── OAKRIDGE ──
  {
    slug: "oakridge",
    name: "Oakridge",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9782, lng: -114.1128 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 17,
      authorizationRequired: true,
      authorizationNote:
        "About 8 km from Calgary and in the same airspace corridor, Oakridge is planned as controlled airspace. Operations run under an Advanced RPAS certificate, and a NAV CANADA RPAS authorisation is obtained wherever the specific site calls for one. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "West wind reaches Oakridge with a running start — it crosses the open water of the Glenmore Reservoir with nothing to slow it, then breaks over the Glenmore Reservoir Escarpment at the community's edge above South Glenmore Park.",
      consequence:
        "On a chinook day the air over that rim is rougher than any street-level check suggests, so an operator working from the forecast alone either flies orbits with visible shimmer in them or cancels at the door.",
      resolution:
        "The gust spread is read on site at the escarpment before anything lifts, the flight is flown in the spread's quiet intervals, and the booking carries a weather hold so the date can move without cost — all inside the standard turnaround.",
    },
    landmarks: [
      "Glenmore Reservoir Escarpment",
      "South Glenmore Park",
      "Southland Leisure Centre",
      "Oakmoor Drive SW",
      "90 Avenue SW",
    ],
    terrain:
      "A mature, level suburb on the table south of the Glenmore Reservoir. Streets like Oakmoor Drive SW run flat to the edge of South Glenmore Park, where the escarpment carries the ground down toward the water.",
    windNote:
      "Chinook belt, and more exposed than it looks: the reservoir gives west wind an open run, and the escarpment lip concentrates it. Conditions deep in the crescents regularly read calmer than the air above the rim.",
    seasonalWindow:
      "Mature canopy drives the look: full leaf from early summer holds the streets in green, fall turns it quickly, and after leaf-drop the frame opens so the escarpment and the water read clearly. Evening light works best across the park edge.",
    bylawNote:
      "Drone takeoff and landing inside Calgary's municipal parks and pathway network is restricted by city bylaw — South Glenmore Park included — so Oakridge launches are staged from private property with the owner's permission.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Same-day scheduling is realistic once the authorisation is in hand, and a visit here pairs naturally with Woodbine, Millrise or North Glenmore Park across the reservoir.",
    faqs: [
      {
        question: "Is Oakridge inside Calgary's controlled airspace?",
        answer:
          "It is planned that way. Oakridge sits in the Calgary airspace corridor treated as Class C controlled airspace for Calgary International, about 17 km away. Work runs under an Advanced RPAS certificate, a NAV CANADA authorisation is obtained wherever the specific site requires one, and NAV Drone confirms the site and altitude first.",
      },
      {
        question: "Why would wind cancel an Oakridge shoot when the forecast looked fine?",
        answer:
          "Because the escarpment edge behaves differently from the street. West wind crosses the open Glenmore Reservoir unbroken, and it arrives at the rim above South Glenmore Park carrying gusts the community's interior never feels. The decision is made from the gust spread measured on site, not from a city-wide forecast.",
      },
      {
        question: "What does an aerial frame add for an Oakridge listing?",
        answer:
          "Position. From the street, an Oakridge address looks like any mature suburb; from altitude, the frame shows how close the property sits to South Glenmore Park and the water beyond the escarpment. That proximity is the community's quiet premium, and it is invisible in ground-level photography.",
      },
    ],
    primaryKeywords: [
      "drone photography Oakridge",
      "aerial photography Oakridge",
      "Oakridge drone services",
    ],
    nearestLocations: ["woodbine", "millrise", "north-glenmore-park", "lakeview"],
  },

  // ─────────────────────────────────────────────────────────── PATTERSON ──
  {
    slug: "patterson",
    name: "Patterson",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0537, lng: -114.1621 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 13,
      authorizationRequired: true,
      authorizationNote:
        "Patterson sits about 13 km from Calgary International — near the charted boundary of the CYYC control zone, close enough that which side of the line a given address falls on decides the paperwork. It is planned as controlled airspace until NAV Drone shows otherwise for the specific site, and where authorisation is required it is filed before the date is booked. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Patterson faces the morning. The community steps down the valley wall toward Edworthy Park, and at the end of the day the high ground behind it takes the sun first — a shadow line walks down through Patterson Heights SW while Edworthy Park below is still lit.",
      consequence:
        "An evening shoot booked off the published sunset time can arrive to find the address already in terrain shadow while the valley floor still glows — on this slope, golden hour belongs to elevations, not to the clock.",
      resolution:
        "The flight is timed to the address's own place on the hillside: a hold flown in the minutes that terrace carries direct light, planned from the terrain rather than the almanac, delivered in the standard turnaround.",
    },
    landmarks: [
      "Edworthy Park",
      "Patterson Pathway",
      "Bow Trail",
      "Patterson Boulevard SW",
      "Optimist Athletic Park",
    ],
    terrain:
      "A terraced hillside community on the west wall of the Bow valley. Streets stack from Patterson Boulevard SW up through Patterson Heights SW, and the ground runs down through trees to Edworthy Park and the river flats.",
    windNote:
      "Chinook belt. West wind comes over the high ground above the community and can tumble on the lee slope, so the air over the terraces is often less settled than the same wind over flat ground nearby.",
    seasonalWindow:
      "Aspect drives the calendar: the east-facing slope takes strong morning light year-round, and evening shoots run earlier here than the sunset time suggests. Snow lingers in the treed draws toward Edworthy Park after the open terraces have cleared.",
    bylawNote:
      "Calgary's parks bylaws keep drone launches out of municipal parks and off pathways — Edworthy Park and the Patterson Pathway included — so takeoff and landing are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Short enough to hit a precise light window on the slope, and a Patterson visit pairs cleanly with Coach Hill, Signal Hill or Cougar Ridge.",
    faqs: [
      {
        question: "Which side of the CYYC control zone boundary is Patterson on?",
        answer:
          "Close enough that the answer is address-specific. Patterson sits about 13 km from Calgary International, near the charted boundary of the CYYC control zone, so it is planned as controlled airspace until NAV Drone shows otherwise for the specific site. Where authorisation is required, it is filed before the date is booked.",
      },
      {
        question: "What time of day should a Patterson property be photographed?",
        answer:
          "Morning for reach, carefully-timed evening for warmth. The community faces east over Edworthy Park, so morning light finds every terrace; in the evening the high ground behind the community casts shadow downhill well before published sunset. The flight is scheduled to the address's elevation, not to the almanac.",
      },
      {
        question: "Can an aerial shoot show how close Patterson sits to the river valley?",
        answer:
          "Yes — that is the community's strongest frame. From altitude, one image holds the terraces, the treed slope and Edworthy Park below, a relationship no ground-level photograph can connect. For addresses on Patterson Heights SW or Patterson View SW, that connection is the reason buyers pay for the slope.",
      },
    ],
    primaryKeywords: [
      "drone photography Patterson",
      "aerial photography Patterson",
      "Patterson drone services",
    ],
    nearestLocations: ["coach-hill", "signal-hill", "cougar-ridge"],
  },

  // ─────────────────────────────────────────────────────────── SHAWNESSY ──
  {
    slug: "shawnessy",
    name: "Shawnessy",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9366, lng: -114.0741 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 15,
      authorizationRequired: false,
      authorizationNote:
        "Shawnessy sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 15 km from Kirkby Field (Indus). Authorisation is not normally required here. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "There is almost no relief in Shawnessy — from the Shawnessy Shopping Centre flats out to James McKevitt Road SW the ground barely moves, and the nearest real break in the prairie is where Fish Creek Provincial Park drops away north of the community.",
      consequence:
        "At ground level everything here is photographed from the same eye height, so the retail acreage, the LRT platform and the rooftops compress into one plane and the scale of the place disappears.",
      resolution:
        "Altitude supplies the relief the terrain withholds: a high oblique separates Shawnessy Shopping Centre from the residential grid and puts Macleod Trail SW in context, and repeatable waypoints keep a progress series comparable — delivered in the standard turnaround.",
    },
    landmarks: [
      "Shawnessy Shopping Centre",
      "Shawnessy LRT Station",
      "Fish Creek Provincial Park",
      "Macleod Trail SW",
      "162 Avenue SW",
    ],
    terrain:
      "Level prairie table in Calgary's deep south. Big-format retail holds the corner of Macleod Trail SW and 162 Avenue SW, residential streets fill the ground beyond it, and the Fish Creek valley is the nearest change in elevation.",
    windNote:
      "Chinook belt with no terrain shelter: west wind crosses the flat table at full strength, and the open retail lands give it nothing to break against. Winter flights are judged on gust spread rather than average speed.",
    seasonalWindow:
      "Flat and open, so snow leaves quickly after a chinook and returns just as fast. Low winter sun throws long shadows across the retail roofs, summer evenings give the cleanest light on the residential grid, and late-summer wildfire smoke is the main contrast risk.",
    bylawNote:
      "City of Calgary bylaws restrict drone takeoff and landing in municipal parks and along pathways, and Fish Creek Provincial Park carries its own provincial rules — Shawnessy launches are staged from private property with permission.",
    subjectMix: ["commercial-retail", "municipal-infrastructure", "new-build-subdivision"],
    mobilisationNote:
      "About 20 minutes from the Calgary base in routine Class G airspace, so same-day work is realistic. One mobilisation covers Shawnessy together with Millrise, Somerset, Evergreen or Bridlewood.",
    faqs: [
      {
        question: "Is a NAV CANADA authorisation needed for a Shawnessy drone shoot?",
        answer:
          "Normally, no. Shawnessy lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 15 km from Kirkby Field at Indus, so no airspace authorisation is usually required. The specific site and altitude are still confirmed against NAV Drone before every flight, which keeps the booking honest.",
      },
      {
        question: "How do you photograph a retail property in a place this flat?",
        answer:
          "From above, at an angle. On level prairie a ground camera puts every building on the same horizon; a high oblique restores depth, showing Shawnessy Shopping Centre, the LRT station and the arterial frontage in one legible frame. For leasing and marketing work, that vantage is the whole argument.",
      },
      {
        question: "Can you fly the same Shawnessy route for repeat progress shots?",
        answer:
          "Yes. The flight plan is saved as waypoints, so each visit reproduces the previous altitudes and angles and the series reads as one sequence rather than a set of unrelated photographs. Routine airspace and a drive of about 20 minutes keep repeat visits simple to schedule.",
      },
    ],
    primaryKeywords: [
      "drone photography Shawnessy",
      "aerial photography Shawnessy",
      "Shawnessy drone services",
    ],
    nearestLocations: ["millrise", "somerset", "evergreen", "bridlewood"],
  },

  // ─────────────────────────────────────────────────────────── SIGNAL HILL ──
  {
    slug: "signal-hill",
    name: "Signal Hill",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0356, lng: -114.1846 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Controlled — between CYYC and CYBW",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 15,
      authorizationRequired: true,
      authorizationNote:
        "About 2 km from Aspen Woods and in the same airspace corridor, Signal Hill is planned as controlled airspace. Operations run under an Advanced RPAS certificate, and a NAV CANADA RPAS authorisation is obtained wherever the specific site calls for one. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "After a mid-winter chinook, Signal Hill wears two seasons at once: wind strips the open slope around Battalion Park back to grass within a day, while the shaded courts off Sierra Morena Boulevard SW hold their snow for weeks.",
      consequence:
        "A winter shoot that lands in that in-between gets mottled brown-and-white ground — the one state that flatters neither a listing nor a retail campaign, and the state the community sits in for much of the season.",
      resolution:
        "The date is watched against the chinook cycle and flown on a clean state — uniform snow or clear grass — with the booking built to slide a day or two either way, still inside the standard turnaround.",
    },
    landmarks: [
      "Battalion Park",
      "Westhills Towne Centre",
      "Signal Hill Centre",
      "Sierra Morena Boulevard SW",
    ],
    terrain:
      "A hillside district on Calgary's west side: the ground climbs from the open slope at Battalion Park to the retail plateaus of Westhills Towne Centre and Signal Hill Centre, with residential streets banked in between.",
    windNote:
      "Chinook belt on exposed high ground. The open slope gives west wind a clear run, and there is little terrain upwind to take the edge off it; winter flights are planned around the gust cycle rather than the daily average.",
    seasonalWindow:
      "Snow cover here is binary and short-lived — chinooks clear the open slope fast while shaded pockets lag — so clean winter frames are a timing exercise. Green-up in spring and long summer evenings are the dependable windows.",
    bylawNote:
      "Takeoff and landing in Calgary's municipal parks — Battalion Park included — is restricted under the city's parks bylaws, so Signal Hill flights launch from private property with the owner's consent.",
    subjectMix: ["commercial-retail", "luxury-residential", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. The drive is short enough to chase a clean-snow morning on a day's notice, and a visit pairs well with Discovery Ridge, Coach Hill or Patterson.",
    faqs: [
      {
        question: "What airspace does Signal Hill fall under for drone work?",
        answer:
          "Controlled airspace between the Calgary International and Springbank zones, about 15 km from Springbank Airport. Flights are planned as controlled airspace under an Advanced RPAS certificate, with a NAV CANADA authorisation obtained wherever the specific site requires one, and NAV Drone confirms every site and altitude before flight.",
      },
      {
        question: "When should a winter shoot in Signal Hill be scheduled?",
        answer:
          "Immediately after weather, not on a fixed date. Chinooks strip the open slope near Battalion Park within a day while shaded streets keep snow for weeks, so the community spends much of winter half-and-half. Bookings here are set to a window and flown when the ground is uniform.",
      },
      {
        question: "Can one flight cover both Westhills Towne Centre and Signal Hill Centre?",
        answer:
          "They are close neighbours on the same retail plateau, so a single mobilisation covers both — though each property gets its own flight plan and its own frames. For leasing work, the useful image is the oblique that shows the two centres and the hillside catchment they share.",
      },
    ],
    primaryKeywords: [
      "drone photography Signal Hill",
      "aerial photography Signal Hill",
      "Signal Hill drone services",
    ],
    nearestLocations: ["discovery-ridge", "coach-hill", "patterson"],
  },

  // ─────────────────────────────────────────────────────────── SILVERADO ──
  {
    slug: "silverado",
    name: "Silverado",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.8975, lng: -114.1004 },
    tier: 2,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 17,
      authorizationRequired: false,
      authorizationNote:
        "Silverado lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 17 km from Kirkby Field (Indus). No airspace authorisation is normally required. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "By the time the city reaches Silverado the controlled airspace has run out: the community sits at Calgary's southern rim in uncontrolled Class G air, 17 km from the nearest aerodrome at Kirkby Field, with the CYYC control zone left well to the north.",
      consequence:
        "Clients quoted by inner-city operators inherit inner-city assumptions — authorisation lead times, paperwork contingencies, padded schedules — none of which applies at this address, so dates get set further out than the airspace ever required.",
      resolution:
        "The per-site NAV Drone check still runs, but with no authorisation to wait on, a Silverado booking moves at the speed of the forecast — a golden-hour slot can be taken on short notice and delivered in the standard turnaround.",
    },
    landmarks: [
      "Spruce Meadows",
      "Township Shopping Centre",
      "Bridlewood-Somerset Escarpment",
      "Silverado Boulevard SW",
      "Sheriff King Street SW",
    ],
    terrain:
      "High, open prairie at the city's southern edge. New residential phases run out toward the open ground near Spruce Meadows, the Bridlewood-Somerset Escarpment marks the rise toward the neighbouring communities, and the retail block at Township Shopping Centre anchors the built edge.",
    windNote:
      "Chinook belt at the city's open edge: with prairie running unbroken to the west, wind arrives at Silverado at full strength. The escarpment adds local stir along its lip, but mostly this is honest, unsheltered chinook country.",
    seasonalWindow:
      "A young community with little canopy, so the seasons register through the ground and the build phases: spring green-up on new sod, long summer light across open prairie, and fast snow turnover in chinook winters. Construction activity photographs cleanly year-round.",
    bylawNote:
      "Under City of Calgary parks bylaws, drone takeoff and landing are restricted in municipal parks and on pathways, so Silverado flights are launched from private property — a builder's site or a client's lot — with consent.",
    subjectMix: ["new-build-subdivision", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 25 minutes from the Calgary base. One mobilisation covers Silverado together with Evergreen, Bridlewood or Shawnessy.",
    faqs: [
      {
        question: "Does flying a drone in Silverado involve airport paperwork?",
        answer:
          "No — not normally. Silverado lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 17 km from Kirkby Field at Indus, so no airspace authorisation is usually required. Each specific site and altitude is still verified against NAV Drone before the flight goes ahead.",
      },
      {
        question: "How fast can a Silverado shoot be turned around from first contact?",
        answer:
          "As fast as the weather cooperates. With routine airspace there is no authorisation to wait on, so the gate is the forecast: a clear evening can be taken on short notice, and the drive from the base runs about 25 minutes. Delivery follows in the standard turnaround.",
      },
      {
        question: "Is Silverado a good candidate for construction progress photography?",
        answer:
          "Yes. The community is still building out, the airspace is routine, and saved waypoints let each visit repeat the last one exactly, so a phase-by-phase series stays comparable. Open prairie light and low obstacles make consistent framing straightforward at any point in the year.",
      },
    ],
    primaryKeywords: [
      "drone photography Silverado",
      "aerial photography Silverado",
      "Silverado drone services",
    ],
    nearestLocations: ["evergreen", "bridlewood", "shawnessy"],
  },

  // ──────────────────────────────────────────────────────────── SOMERSET ──
  {
    slug: "somerset",
    name: "Somerset",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9256, lng: -114.0908 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 16,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 16 km from Kirkby Field (Indus), Somerset lies outside the charted Calgary-area control zones, and no authorisation is normally required. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Somerset holds no water of its own — the grid off Somerset Drive SW is dry all the way to its edges — and the nearest natural water a frame can reach is the creek inside Fish Creek Provincial Park, a wooded line on the northern horizon.",
      consequence:
        "From the street that park might as well not exist: a ground-level listing shows a suburb indistinguishable from its neighbours, with nothing to say the valley sits a few minutes away — which is precisely the context a deep-south buyer is weighing.",
      resolution:
        "An elevated frame looking north carries the Fish Creek treeline across the horizon and places the address at its true distance from the park — honest context, no over-claim, delivered in the standard turnaround.",
    },
    landmarks: [
      "Somerset-Bridlewood LRT Station",
      "Fish Creek Provincial Park",
      "Cardel Rec South",
      "Somerset Drive SW",
      "162 Avenue SW",
    ],
    terrain:
      "A level residential grid at Calgary's southern edge, anchored by the Somerset-Bridlewood LRT Station and the recreation block at Cardel Rec South, with the wooded Fish Creek valley lying beyond the communities to its north.",
    windNote:
      "Chinook belt, flat and largely unsheltered: west wind crosses the grid at strength, and only the distant Fish Creek valley breaks the surrounding table. Winter flight windows are chosen on gust behaviour, not the headline speed.",
    seasonalWindow:
      "The grid greens through spring and holds colour all summer; after leaf-drop the sightline to the Fish Creek treeline opens and the park reads more strongly in north-facing frames. Long June and July evenings give the softest light on rooflines.",
    bylawNote:
      "City of Calgary parks bylaws restrict where a drone may take off and land — municipal parks and pathways are out — so Somerset flights launch from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "municipal-infrastructure", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the Calgary base in routine airspace, so a booking here is a light exercise in scheduling. Somerset pairs on a single trip with Millrise, Shawnessy, Bridlewood or Evergreen.",
    faqs: [
      {
        question: "What are the airspace rules for a drone shoot in Somerset?",
        answer:
          "Somerset is uncontrolled Class G airspace, outside the charted Calgary-area control zones and about 16 km from Kirkby Field at Indus. No airspace authorisation is normally required, which removes the main scheduling constraint that applies to inner-city Calgary. The specific site and altitude are confirmed against NAV Drone before every flight.",
      },
      {
        question: "Can an aerial photo show how close Somerset is to Fish Creek Provincial Park?",
        answer:
          "Yes, and honestly. From altitude, a north-facing frame carries the park's treeline on the horizon, showing the real relationship without implying park-edge frontage the address does not have. That single contextual image gives a Somerset listing the outdoor setting a street-level photograph cannot supply.",
      },
    ],
    primaryKeywords: [
      "drone photography Somerset",
      "aerial photography Somerset",
      "Somerset drone services",
    ],
    nearestLocations: ["millrise", "shawnessy", "bridlewood", "evergreen"],
  },

  // ────────────────────────────────────────────────────── SPRINGBANK HILL ──
  {
    slug: "springbank-hill",
    name: "Springbank Hill",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0486, lng: -114.1774 },
    tier: 3,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 14,
      authorizationRequired: true,
      authorizationNote:
        "At about 14 km from Calgary International, Springbank Hill runs close to the charted edge of the CYYC control zone — which side of the line an address falls on decides the paperwork. Planning treats it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Springbank Hill's street network never resolves into a grid: from altitude, Springbank Way SW and Slopeview Drive SW wander with the contours, and whole blocks stop where the ravines of Strathcona Ravines Park cut through the plan.",
      consequence:
        "That geometry defeats ground photography — a buyer cannot place the address inside the folds of the network, or see how near it sits to the ravine greens or to Aspen Landing Shopping Centre — so the location's value goes unstated.",
      resolution:
        "An approach run flown along the collector, or an orbit that holds the address and its ravine in one frame, makes the position legible at a glance — planned around the control-zone check and delivered in the standard turnaround.",
    },
    landmarks: [
      "Strathcona Ravines Park",
      "Griffith Woods Park",
      "Aspen Landing Shopping Centre",
      "Slopeview Drive SW",
      "Springbank Way SW",
    ],
    terrain:
      "High ground on Calgary's western edge, cut by wooded ravines. The land rolls between Strathcona Ravines Park and the forest of Griffith Woods Park at its southern reach, with newer streets like Springbluff Boulevard SW following the contours rather than a survey line.",
    windNote:
      "Chinook belt on elevated, open terrain: west wind meets Springbank Hill early and largely unbroken. The ravines add local swirl along their edges, so flight lines near the tree gaps are planned with extra margin.",
    seasonalWindow:
      "The ravines hold the seasons longest — snow lingers in the wooded cuts after the open slopes clear, and their canopy turns ahead of the street plantings in fall — so the community's frames change character month to month. Summer evenings light the high ground late and well.",
    bylawNote:
      "Calgary's parks bylaws restrict drone takeoff and landing in municipal parks, ravine lands and pathways, so Springbank Hill launches are staged from private property with the owner's permission.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "construction-progress"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Once the airspace check clears, scheduling is straightforward, and a visit pairs naturally with Aspen Woods, Discovery Ridge or West Springs next door.",
    faqs: [
      {
        question: "How does the CYYC control-zone boundary affect Springbank Hill?",
        answer:
          "Springbank Hill sits about 14 km from Calgary International, near the charted edge of the CYYC control zone — close enough that which side of the line an address falls on decides the paperwork. It is planned as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before booking.",
      },
      {
        question: "Why is Springbank Hill hard to photograph from the ground?",
        answer:
          "The street plan follows the terrain, not a grid, and the ravines interrupt it. A ground photograph cannot show where an address sits within those folds or how close it lies to Strathcona Ravines Park, so the position a buyer is paying for never appears in the frame.",
      },
      {
        question: "Can one aerial session cover a Springbank Hill home and its surroundings?",
        answer:
          "Yes. A single flight can hold the address, its ravine edge and the shopping frontage at Aspen Landing in related frames, which is usually the full context a listing needs. The community also pairs with Aspen Woods or West Springs when more coverage is wanted on the same trip.",
      },
    ],
    primaryKeywords: [
      "drone photography Springbank Hill",
      "aerial photography Springbank Hill",
      "Springbank Hill drone services",
    ],
    nearestLocations: ["aspen-woods", "discovery-ridge", "west-springs"],
  },

  // ────────────────────────────────────────────────────── STRATHCONA PARK ──
  {
    slug: "strathcona-park",
    name: "Strathcona Park",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0447, lng: -114.1611 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 13,
      authorizationRequired: true,
      authorizationNote:
        "At about 13 km from Calgary International, Strathcona Park runs close to the charted edge of the CYYC control zone — which side of the line an address falls on decides the paperwork. Planning treats it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Strathcona Park reads as a quiet interior behind fast edges: Bow Trail SW and Sarcee Trail SW carry the traffic along its boundaries while, inside, Strathcona Drive SW winds through terrain the ravine keeps any through road from crossing.",
      consequence:
        "A buyer who hears the arterial names pictures noise at the fence line, and a ground photograph can neither confirm nor refute it — the separation between an address and those roads is exactly the thing street-level imagery cannot measure.",
      resolution:
        "An orbit that holds the address with the Strathcona Park Ravine between it and the arterial shows the buffer plainly — one honest frame settling the question, delivered in the standard turnaround.",
    },
    landmarks: [
      "Edworthy Park",
      "Strathcona Park Ravine",
      "Bow Trail SW",
      "Sarcee Trail SW",
      "Strathcona Drive SW",
    ],
    terrain:
      "A ravine-cut terrace on the rim of the Bow valley. The wooded line of the Strathcona Park Ravine threads the interior, and the ground falls away past the community's edge toward Edworthy Park and the river below.",
    windNote:
      "Chinook belt at the valley rim: west wind runs over the terrace and drops toward Edworthy Park, and the ravine cuts channel gusts locally. Air along the rim is generally livelier than in the sheltered crescents.",
    seasonalWindow:
      "The ravine sets the palette — full canopy through summer, a fast colour turn in autumn, and bare branches that open sightlines toward the valley for winter frames. Evening light reaches the terrace late and warm.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in parks, ravines and along pathways, so Strathcona Park flights launch from private property with the client's consent.",
    subjectMix: ["luxury-residential", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the Calgary base — close enough to hit a precise evening window. One mobilisation pairs Strathcona Park with Patterson, Coach Hill, Signal Hill or Discovery Ridge.",
    faqs: [
      {
        question: "Does a Strathcona Park shoot need airspace authorisation before booking?",
        answer:
          "Sometimes — it depends on the exact address. The community sits about 13 km from Calgary International, near the charted CYYC control-zone boundary, so it is planned as controlled airspace until NAV Drone shows otherwise for the specific site. Where authorisation is required, it is filed before the date is confirmed.",
      },
      {
        question: "Can aerial photography show how quiet a Strathcona Park address actually is?",
        answer:
          "It can show the reason: distance and the ravine. An elevated frame places the address, the wooded Strathcona Park Ravine and the arterial in one image, so the buffer between home and traffic is visible rather than asserted. That is the community's case, made in a single photograph.",
      },
      {
        question: "What is the best season to photograph the ravine setting?",
        answer:
          "Two windows work. Full summer canopy shows the green corridor at its strongest, while late autumn — after leaf-drop — opens sightlines through the ravine toward Edworthy Park and the valley. Winter snow reads cleanly too; the choice depends on whether the listing sells enclosure or outlook.",
      },
    ],
    primaryKeywords: [
      "drone photography Strathcona Park",
      "aerial photography Strathcona Park",
      "Strathcona Park drone services",
    ],
    nearestLocations: ["discovery-ridge", "patterson", "coach-hill", "signal-hill"],
  },

  // ───────────────────────────────────────────────────────── WEST SPRINGS ──
  {
    slug: "west-springs",
    name: "West Springs",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0713, lng: -114.1917 },
    tier: 3,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 14,
      authorizationRequired: true,
      authorizationNote:
        "At about 14 km from Calgary International, West Springs runs close to the charted edge of the CYYC control zone — which side of the line an address falls on decides the paperwork. Planning treats it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "West Springs is young from the air: along West Grove Rise SW and Westland Road SW the trees have not yet grown into the streetscape, so the seasons here register through the ground plane — lawn green, shoulder-season brown, fresh snow — rather than through canopy colour.",
      consequence:
        "Autumn, which flatters mature communities, does little here: an October frame catches neither summer green nor winter white, and new streets photographed on bare sod read unfinished even when the homes are not.",
      resolution:
        "Shoots are slotted to the ground plane's strong states — full green or fresh snow — and a build-progress series is scheduled the same way, so the community photographs at its best in every frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "Calgary Farmers' Market West",
      "Aspen Landing Shopping Centre",
      "West Springs School",
      "West Grove Rise SW",
      "Westland Road SW",
    ],
    terrain:
      "Gently rolling upland at the city's western limit. New and recent phases spread between West Springs School and the retail edge at Aspen Landing Shopping Centre, with open ground still visible between the built pockets.",
    windNote:
      "Chinook belt at the west edge of the city: wind off the foothills arrives here early, with little built or natural shelter upwind. Winter flight windows follow the gust cycle rather than the daily average.",
    seasonalWindow:
      "With little canopy, light does the work: long summer evenings model the rooflines cleanly, fresh snow gives the sharpest winter frames, and the brown shoulder weeks are avoided by scheduling. Open slopes clear snow days ahead of shaded ground.",
    bylawNote:
      "Drone takeoff and landing in Calgary's municipal parks and on public pathways is restricted by bylaw, so West Springs launches are staged from private property — commonly a builder's lot or the client's own — with consent.",
    subjectMix: [
      "luxury-residential",
      "new-build-subdivision",
      "commercial-retail",
      "construction-progress",
    ],
    mobilisationNote:
      "About 20 minutes from the Calgary base. A West Springs visit pairs on one mobilisation with Aspen Woods, Cougar Ridge, Discovery Ridge or Springbank Hill.",
    faqs: [
      {
        question: "Is West Springs controlled or uncontrolled airspace for drones?",
        answer:
          "It is treated as controlled until proven otherwise. West Springs sits about 14 km from Calgary International, near the charted CYYC control-zone boundary, where the exact address decides the paperwork. NAV Drone is checked for the specific site, and any required authorisation is filed before the shoot date is booked.",
      },
      {
        question: "When does a new-build West Springs street photograph best from the air?",
        answer:
          "In one of two states: full summer green, once sod and early landscaping have taken, or clean fresh snow, which unifies unfinished ground. The shoulder weeks between them read as bare earth and are avoided. Scheduling to those windows costs nothing and changes the result completely.",
      },
    ],
    primaryKeywords: [
      "drone photography West Springs",
      "aerial photography West Springs",
      "West Springs drone services",
    ],
    nearestLocations: ["aspen-woods", "cougar-ridge", "discovery-ridge", "springbank-hill"],
  },

  // ──────────────────────────────────────────────────────────── WOODBINE ──
  {
    slug: "woodbine",
    name: "Woodbine",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 50.9558, lng: -114.1093 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 18,
      authorizationRequired: false,
      authorizationNote:
        "Woodbine sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 18 km from Kirkby Field (Indus). Authorisation is not normally required here. Flight plans here also keep clear of Rockyview General Hospital heliport, operating about 4 km away. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Rockyview General Hospital heliport"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "The busiest aircraft near Woodbine do not use an airport: the Rockyview General Hospital heliport works about 4 km away, while the nearest aerodrome — Kirkby Field at Indus — lies 18 km east and the community's own air is uncontrolled Class G.",
      consequence:
        "An operator who reads Class G and stops there plans as if the sky were empty — but an approach path to a hospital pad is exactly the traffic a flight plan must already know about, and the client carries the risk when it does not.",
      resolution:
        "Every Woodbine plan is drawn clear of the heliport approaches and confirmed against NAV Drone for the specific site — after which the routine airspace does its job, and the shoot books quickly and delivers in the standard turnaround.",
    },
    landmarks: [
      "Fish Creek Provincial Park",
      "Woodbine Square",
      "Anderson LRT Station",
      "Woodpark Boulevard SW",
      "24 Street SW",
    ],
    terrain:
      "A mature, level table ending at the Fish Creek valley: streets like Wood Valley Drive SW run out to the rim where the ground breaks into the wooded park, and the retail block at Woodbine Square anchors the interior.",
    windNote:
      "Chinook belt. The flat table takes west wind at full strength, and the Fish Creek valley along the community's park edge gives gusts a channel to run in, so conditions at the rim can read livelier than mid-grid.",
    seasonalWindow:
      "The park edge drives the frames: full canopy along Fish Creek through summer, a strong colour turn in autumn, and open winter sightlines into the valley once the leaves are down. Evening light across the table is reliable and long in mid-summer.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and pathway corridors, and Fish Creek Provincial Park has provincial rules of its own — Woodbine launches stage from private property with consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the Calgary base, with no authorisation lead time to plan around. Woodbine pairs on one mobilisation with Millrise, Oakridge or Somerset.",
    faqs: [
      {
        question: "How does the Rockyview General Hospital heliport affect drone flights over Woodbine?",
        answer:
          "It shapes the flight plan rather than requiring authorisation. Woodbine is uncontrolled Class G airspace about 18 km from Kirkby Field at Indus, so no authorisation is normally needed — but the hospital heliport operates about 4 km away, and every plan here keeps clear of its approaches and is confirmed against NAV Drone.",
      },
      {
        question: "What does altitude add to a Woodbine listing beside Fish Creek?",
        answer:
          "The park boundary. From the air, one frame holds the address, the rim and the wooded valley beyond it, proving how close the property sits to Fish Creek Provincial Park. Ground photography can gesture at the trees; the aerial frame shows the actual relationship, honestly and at a glance.",
      },
      {
        question: "Can a Woodbine shoot be arranged on short notice?",
        answer:
          "Usually, yes. The airspace is routine, so there is no authorisation to wait for; the drive from the base runs about 20 minutes; and the constraint left is weather. When a forecast window opens, the flight can take it, with delivery following in the standard turnaround.",
      },
    ],
    primaryKeywords: [
      "drone photography Woodbine",
      "aerial photography Woodbine",
      "Woodbine drone services",
    ],
    nearestLocations: ["millrise", "oakridge", "somerset"],
  }
];
